import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './RightSidePanel.css';

interface RightSidePanelProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  onSaveDraft?: () => void;
  wide?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * RightSidePanel Component
 * Slide-out panel from the right side of the screen.
 * Scrollable, dims background when open, saves draft on close.
 */
const RightSidePanel: React.FC<RightSidePanelProps> = ({
  isOpen,
  title,
  onClose,
  onSaveDraft,
  wide = false,
  className = '',
  children,
}) => {
  const handleClose = useCallback(() => {
    if (onSaveDraft) {
      onSaveDraft();
    }
    onClose();
  }, [onClose, onSaveDraft]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const panel = (
    <>
      {isOpen && (
        <div
          className="pms-panel-overlay"
          onClick={handleClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={`pms-right-side-panel ${isOpen ? 'pms-right-side-panel--open' : ''} ${wide ? 'pms-right-side-panel--wide' : ''} ${title.trim() ? '' : 'pms-right-side-panel--no-title'} ${className}`.trim()}
        role="dialog"
        aria-modal={isOpen}
        aria-label={title.trim() || 'Side panel'}
      >
        <div className="pms-panel-header">
          {title.trim() && <h2 className="pms-panel-title">{title}</h2>}
          <button
            className="pms-panel-close"
            onClick={handleClose}
            aria-label="Close panel"
            type="button"
          >
            <span className="pms-panel-close__icon" aria-hidden="true">
              X
            </span>
            <span className="pms-panel-close__label">Close</span>
          </button>
        </div>
        <div className="pms-panel-content">
          {children}
        </div>
      </aside>
    </>
  );

  return createPortal(panel, document.body);
};

export default RightSidePanel;
